import { Layout } from "@/components/Layout/Layout";
import { useEffect, useState } from "react";
import { MainHeading } from "@/components/common/MainHeading";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { ICashSettlementHistory, ICashSettlementHistoryResponse } from "@/interfaces/CashSettlement/cashSettlement.interface";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import { IFilter } from "@/interfaces/common.interface";
import { formatDateTime, formatRand, GetFilterUrl } from "@/utility/helper";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import { History, Search, Wallet, Hash, CreditCard, Clock } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { RecordSettlementModal } from "@/components/common/Model/RecordSettlementModal";
import { SettlementOrdersModal } from "@/components/common/Model/SettlementOrdersModal";
import { TableFilters } from "@/components/common/TableFilters";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";

export const CashSettlement = () => {
    const { canCreate } = usePermissions();
    const [history, setHistory] = useState<ICashSettlementHistory[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [seeHistoryType, setSeeHistoryType] = useState<'pending' | 'collected' | 'settled' | null>(null);
    const [selectedHistoryDriverId, setSelectedHistoryDriverId] = useState<string | null>(null);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo } = useFilter<IFilter>({
        page: 1,
        limit: 10,
    });

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<ICashSettlementHistoryResponse>(
                `${API_URL}${API_ENDPOINT.cashSettlement}/history?${queryParams}`,
                { method: 'GET' }
            );
            setHistory(response?.data?.settlements || []);
            setPageInfo(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search]);

    const columns: TableColumn<ICashSettlementHistory>[] = [
        {
            key: "id",
            header: "Settlement ID",
            render: (item) => <div className="text-xs font-mono text-primary flex items-center gap-2"><Hash className="w-3 h-3" /> {item.settlementId}</div>,
        },
        {
            key: "driver",
            header: "Driver",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0 border border-primary/20">
                        {item.driver?.fullName?.charAt(0)?.toUpperCase() || 'D'}
                    </div>
                    <div>
                        <p className="font-semibold text-foreground text-sm">{item.driver?.fullName}</p>
                        <p className="text-[11px] text-muted-foreground">{item.driver?.email}</p>
                    </div>
                </div>
            ),
        },
        {
            key: "totalOrders",
            header: "Orders",
            render: (item) => (
                <button
                    onClick={() => {
                        setSelectedHistoryDriverId(item.driverId);
                        setSeeHistoryType("settled");
                    }}
                    className="text-sm font-bold text-primary hover:underline hover:text-primary/80 transition-colors flex items-center gap-1 bg-primary/5 px-3 py-1.5 rounded-full cursor-pointer"
                >
                    {item.totalOrders} orders
                </button>
            ),
        },
        {
            key: "totalAmount",
            header: "Amount Settled",
            render: (item) => (
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 text-green-700 rounded-lg">
                        <CreditCard className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-black text-foreground">{formatRand(Number(item.totalAmount))}</span>
                </div>
            ),
        },
        {
            key: "driverEarning",
            header: "Payout",
            render: (item) => <div className="text-sm font-bold text-primary">{formatRand(Number(item.driverEarning || 0))}</div>,
        },
        {
            key: "createdAt",
            header: "Date & Time",
            render: (item) => (
                <div className="flex flex-col">
                    <span className="text-sm text-foreground">{formatDateTime(item.createdAt).split(',')[0]}</span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDateTime(item.createdAt).split(',')[1]}
                    </span>
                </div>
            ),
        },
    ];

    return (
        <Layout>
            <MainHeading
                title="Cash Settlement"
                description="Manage revenue tracking and driver payouts"
                actionLabel={canCreate(RESOURCES.CASH_SETTLEMENT) ? "Record Settlement" : undefined}
                actionIcon={canCreate(RESOURCES.CASH_SETTLEMENT) ? <Wallet className="w-4 h-4" /> : undefined}
                onAction={() => setShowModal(true)}
            />

            <div className="mt-6 space-y-4">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search settlements by driver name or ID..."
                />

                <CommonTable
                    columns={columns}
                    data={history}
                    loading={loading}
                    emptyMessage="No settlement records found."
                />
                <Pagination
                    currentPage={filters.page}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            <RecordSettlementModal
                open={showModal}
                onOpenChange={setShowModal}
                onSuccess={fetchHistory}
                onSeeHistory={(driverId) => {
                    setSelectedHistoryDriverId(driverId);
                    setSeeHistoryType("collected");
                }}
            />

            <SettlementOrdersModal
                open={!!selectedHistoryDriverId}
                onOpenChange={(open) => {
                    if (!open) {
                        setSelectedHistoryDriverId(null);
                        setSeeHistoryType(null);
                    }
                }}
                driverId={selectedHistoryDriverId}
                cashSettlementStatus={seeHistoryType}
            />
        </Layout>
    );
};
;
